<template>

    <div>
        <div class="input-group" v-show="!isSearchable">
            <input type="text" class="form-control" v-model="searchText" readonly>
            <button class="btn btn-info input-group-text" type="button" @click="clearSelected" :disabled="readonly || disabled">
                <fa-icon icon="times-circle" />
            </button>
        </div>
        <div v-show="isSearchable">
            <input ref="ddButton" type="text" class="form-control" :maxlength="maxlength" :placeholder="placeholder"
                   v-model="searchText"
                   @keydown.esc.prevent="hideDropDown"
                   @keydown.up.prevent="hideDropDown"
                   @keydown.down.prevent="downArrow"
                   @keydown.enter.prevent="acceptSelection"
                   @focus="searchFocusIn" @blur="searchFocusOut" :readonly="readonly || disabled">

            <ul ref="ddMenu" class="dropdown-menu" role="menu" data-bs-reference="parent">
                <li role="presentation" v-for="result in searchResults" :class="{ active: isMatch(result) }" :key="result.Key"
                    @keydown.enter.prevent="selectResult(result)">
                    <a class="dropdown-item" role="menuitem" @click.prevent="selectResult(result)">
                        {{ result.Value }}
                    </a>
                </li>
            </ul>
        </div>
    </div>
</template>

<style scoped lang="scss">
    .dropdown-menu {
        li a {
            cursor: pointer;
        }
    }
</style>

<script lang="ts">

import { defineComponent } from "vue";

import { Dropdown } from "bootstrap";

import { DataRequest } from "@/shared/support/Data";

interface Entry {
    Key: string;
    Value: string;
}

export default defineComponent({
    props: {
        options: { type: Object, required: false, default: null },
        modelValue: { type: [String, Number], required: false, default: "" },
        maxlength: { type: Number, required: false, default: 200 },
        searchUrl: { type: String, required: true, default: "" },
        allowNew: { type: Boolean, required: false, default: false },
        maxRecords: { type: Number, required: false, default: 25 },
        readonly: { type: Boolean, required: false, default: false },
        disabled: { type: Boolean, required: false, default: false },
        placeholder: { type: String, required: false, default: "Search Text" },
    },
    emits: ["update:modelValue", "selection"],
    data() {
        return {
            selectedEntry: null as Entry | null,
            searchText: "",
            searchResults: [] as Entry[],
            isFocused: false,
            loading: false,
            dropDownButton: null as Dropdown | null,
            dropDownOpen: false,
            inDropDown: false,
        };
    },
    computed: {
        isSearchable(): boolean {
            return !this.selectedEntry;
        },
    },
    mounted() {
        this.dropDownButton = new Dropdown(this.$refs.ddButton as HTMLButtonElement);
        (this.$refs.ddButton as HTMLButtonElement).addEventListener("hide.bs.dropdown", (event: Event): boolean => {
            this.dropDownOpen = false;
            this.inDropDown = false;
            return true;
        });
        (this.$refs.ddButton as HTMLButtonElement).addEventListener("show.bs.dropdown", (event: Event): boolean => {
            // when we don't have search results, prevent the dropdown from opening
            if (!this.searchResults || this.searchResults.length === 0) {
                event.preventDefault();
                return false;
            } else {
                this.dropDownOpen = true;
                this.inDropDown = false;
            }
            return true;
        });
        document.addEventListener("click", this.handleMouseClicks);
    },
    unmounted() {
        document.removeEventListener("click", this.handleMouseClicks);
    },
    methods: {
        isMatch(entry: Entry): boolean {
            return (this.isFocused || this.inDropDown) && entry.Value.toString().toLowerCase() === this.searchText.toLowerCase();
        },
        showDropDown(): void {
            if (this.searchResults && this.searchResults.length > 0) {
                this.dropDownOpen = true;
                this.inDropDown = false;
                this.dropDownButton?.show();
                this.downArrow();
            } else {
                this.hideDropDown();
            }
        },
        hideDropDown(): void {
            this.dropDownButton?.hide();
            this.dropDownOpen = false;
        },
        downArrow(): void {
            if (!this.dropDownOpen && this.searchResults && this.searchResults.length > 0) {
                this.showDropDown();
            }
        },
        updateSelection(val: string): void {
            if (!this.isFocused) return;        
            
            this.loading = true;
            this.searchResults = [];

            if (this.searchUrl && this.searchText) {
                const dr = new DataRequest();
                dr.$get<Entry[]>(this.searchUrl, { SearchPhrase: this.searchText, MaxRecords: this.maxRecords })
                    .then((result: Entry[]): void => {
                        this.searchResults = result;
                        this.loading = false;
                        this.showDropDown();
                    })
                    .catch((reason): void => {
                        this.loading = false;
                        this.hideDropDown();
                    });
            } else {
                this.hideDropDown();
            }
        },
        acceptSelection(): void {
            this.$emit("update:modelValue", this.selectedEntry?.Key || null);
            this.$emit("selection");
            this.hideDropDown();
        },
        selectResult(entry: Entry): void {
            this.selectedEntry = entry;
            this.searchText = entry.Value;
            this.hideDropDown();
            this.$emit("update:modelValue", this.selectedEntry.Key);
            this.$emit("selection");
        },
        clearSelected(): void {
            this.selectedEntry = null;
            this.searchResults = [];
            if (!this.allowNew) {
                this.searchText = "";
                this.$emit("update:modelValue", null);
                this.$emit("selection");
            }
        },
        searchFocusIn(): void {
            this.isFocused = true;
        },
        searchFocusOut(): void {
            // allow any mouse clicks to be processed before closing the dropdown
            setTimeout((): void => {
                if (this.inDropDown) {
                    this.inDropDown = false;
                } else {
                    this.isFocused = false;
                    this.hideDropDown();
                    if (!this.selectedEntry && !this.allowNew) {
                        this.searchText = "";
                    }
                }
            }, 500);
        },
        handleMouseClicks(event: MouseEvent): boolean {
            // Close the dropdown when clicking outside
            this.inDropDown = false;
            if (this.dropDownOpen) {
                const target = event.target;
                if (target && (this.$refs.ddMenu as HTMLUListElement).contains(target as Node)) {
                    // clicking on dropdown menu
                    this.inDropDown = true;
                } else {
                    event.preventDefault();
                    event.stopPropagation();
                    this.hideDropDown();
                    return false;
                }
            }
            return true;
        },
    },
    watch: {
        searchText(val: string) {
            this.updateSelection(val);
        },
        modelValue(val: string | number): void {
            if (!val) {
                this.selectedEntry = null;
                this.searchText = "";
                this.searchResults = [];
            }
        },
    },
});

</script>
